import React from 'react';
import type { Showcase, ShowcaseProperty, ShowcaseResource } from '../../types';
import { FileText, Download, BookOpen } from 'lucide-react';

interface DocumentsSectionProps {
  showcase: Showcase;
  property: ShowcaseProperty;
  resources: ShowcaseResource[];
  brandColour: string;
  darkMode?: boolean;
}

export default function DocumentsSection({ showcase, property, resources, brandColour, darkMode: dm }: DocumentsSectionProps) {
  const docs = resources.filter((r) => r.source_url);
  if (docs.length === 0) return null;

  const handleDownload = async (url: string, title: string) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${title || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  return (
    <section className={`py-16 ${dm ? 'bg-slate-800' : 'bg-slate-50'}`}>
      <div className="max-w-5xl mx-auto px-8">
        <h2 className={`text-2xl font-bold mb-8 ${dm ? 'text-white' : 'text-slate-900'}`}>Documents</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {docs.map((doc) => (
            <button
              key={doc.id}
              onClick={() => handleDownload(doc.source_url!, doc.title || 'document')}
              className={`flex items-center gap-4 p-5 rounded-xl border hover:shadow-sm transition-all group text-left ${dm ? 'bg-slate-900 border-slate-700 hover:border-slate-600' : 'bg-white border-slate-200 hover:border-slate-300'}`}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: brandColour + '15' }}>
                {doc.type === 'brochure_pdf' ? (
                  <BookOpen className="w-6 h-6" style={{ color: brandColour }} />
                ) : (
                  <FileText className="w-6 h-6" style={{ color: brandColour }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-medium truncate ${dm ? 'text-white' : 'text-slate-900'}`}>{doc.title || 'Document'}</div>
                <div className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>PDF</div>
              </div>
              <Download className={`w-5 h-5 transition-colors ${dm ? 'text-slate-500 group-hover:text-slate-300' : 'text-slate-400 group-hover:text-slate-600'}`} />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
